import {
  Alert,
  type AlertColor,
  Snackbar,
  type SnackbarOrigin,
} from '@mui/material';
import React, { type ReactNode, useRef, useState } from 'react';

import NotificationSound from '@/assets/notification.mp3';
import { NotificationContext, type ShowNotificationOptions } from '@/contexts';
import { logger } from '@/lib/logger';

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('info');
  const [duration, setDuration] = useState(6000);
  const [position, setPosition] = useState<SnackbarOrigin>({
    vertical: 'bottom',
    horizontal: 'right',
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const showNotification = ({
    message,
    severity = 'info',
    duration = 6000,
    playSound = false,
    position = { vertical: 'bottom', horizontal: 'right' },
  }: ShowNotificationOptions) => {
    setMessage(message);
    setSeverity(severity);
    setDuration(duration);
    setPosition(position);
    setOpen(true);

    // Play notification sound if requested
    if (playSound && audioRef.current) {
      try {
        audioRef.current.currentTime = 0; // Reset to beginning
        audioRef.current.play().catch((error) => {
          logger.error('Could not play notification sound:', { error });
        });
      } catch (error) {
        logger.error('Error playing notification sound:', { error });
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={position}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
      {/* Hidden audio element for notification sounds */}
      <audio ref={audioRef} preload="auto" style={{ display: 'none' }}>
        <source src={NotificationSound} type="audio/mp3" />
        Your browser does not support the audio element.
      </audio>
    </NotificationContext.Provider>
  );
};
